<template>
  <div class="page lineage-page">
    <header class="lin-head">
      <p class="lin-kicker">{{ zh ? '創作族譜' : 'Family tree' }}</p>
      <h1>{{ zh ? '每日創作的族譜' : 'A family tree of the daily practice' }}</h1>
      <p class="lin-intro">{{ zh
        ? `${c.nodes} 件程式草圖裡，${c.inFamily} 件彼此有血緣，長成 ${c.families} 個家族與 ${c.pairs} 對雙生；其餘 ${c.isolated} 件是獨立的一天。每棵樹由上往下：最上面是源頭，往下一列是下一代。`
        : `Of ${c.nodes} code sketches, ${c.inFamily} are related, forming ${c.families} families and ${c.pairs} pairs; the other ${c.isolated} stand alone. Each tree reads top-down: the origin at the top, one generation per row.` }}</p>
      <p class="lin-intro lin-intro-td"><a href="#td" @click.prevent="jump('td')">{{ zh ? '2025–26 的 TouchDesigner 系列在下方另成一節：那一系列的血緣在模組裡，不在 fork。↓' : 'The 2025–26 TouchDesigner series has its own section below: its lineage lives in modules, not forks. ↓' }}</a></p>
      <p class="lin-orient" role="group" :aria-label="zh ? '排列方向' : 'Orientation'">
        <span>{{ zh ? '排列' : 'Layout' }}</span>
        <button type="button" :aria-pressed="!horizontal" @click="horizontal = false">{{ zh ? '由上往下' : 'Top-down' }}</button>
        <button type="button" :aria-pressed="horizontal" @click="horizontal = true">{{ zh ? '由左往右' : 'Left-to-right' }}</button>
      </p>
      <p class="lin-legend">
        <span class="lin-key lin-key-fork"><i></i>{{ zh ? `實線＝他按過 fork（${c.forks} 條）` : `solid = forked on OpenProcessing (${c.forks})` }}</span>
        <span class="lin-key lin-key-sim"><i></i>{{ zh ? `虛線＝從程式碼推論的延續（${c.sims} 條）` : `dashed = descent inferred from the code (${c.sims})` }}</span>
        <span class="lin-key lin-key-priv"><i></i>{{ zh ? `未公開的 ${c.private} 件看得到、點不開` : `${c.private} private ones are shown but not linked` }}</span>
      </p>
    </header>

    <!-- 不用 <nav>：App.vue 對 nav 有全站樣式（站頭那條），會把目錄擠成右邊一欄 -->
    <div class="lin-index" role="navigation" :aria-label="zh ? '家族目錄' : 'Family index'">
      <p class="lin-label">{{ zh ? `家族（${big.length}）` : `Families (${big.length})` }}</p>
      <ul>
        <li v-for="f in big" :key="f.id">
          <a :href="'#' + f.id" @click.prevent="jump(f.id)">
            <span class="lin-index-thumb"><img v-if="founder(f).img" :src="founder(f).img" alt="" loading="lazy" width="132" height="92" /></span>
            <span class="lin-index-text">
              <strong>{{ famName(f) }}</strong>
              <em>{{ f.n }} {{ zh ? '件' : '' }} · {{ f.span[0].slice(0, 4) }}<template v-if="f.span[1].slice(0, 4) !== f.span[0].slice(0, 4)">–{{ f.span[1].slice(2, 4) }}</template></em>
            </span>
          </a>
        </li>
      </ul>
    </div>

    <section v-for="f in big" :key="f.id" :id="f.id" class="lin-family">
      <header class="lin-family-head">
        <div>
          <p class="lin-label">{{ f.no }} · {{ f.trees.length > 1 ? (zh ? `${f.trees.length} 棵樹` : `${f.trees.length} trees`) : (zh ? '一棵樹' : 'one tree') }}</p>
          <h2>{{ famName(f) }}<small v-if="f.nameZh && !zh"> {{ f.nameZh }}</small><small v-else-if="f.nameZh && f.name !== f.nameZh"> {{ f.name }}</small></h2>
          <p class="lin-family-meta">
            {{ f.n }} {{ zh ? '件' : 'works' }} · {{ f.span[0] }} → {{ f.span[1] }}
            <template v-if="f.forks"> · {{ zh ? `fork ${f.forks}` : `${f.forks} forks` }}</template>
            <template v-if="f.sims"> · {{ zh ? `推論 ${f.sims}` : `${f.sims} inferred` }}</template>
            <template v-if="f.priv"> · {{ zh ? `未公開 ${f.priv}` : `${f.priv} private` }}</template>
          </p>
          <p v-if="f.stems.length" class="lin-family-stems">{{ zh ? '分支' : 'Branches' }}：{{ f.stems.join(' · ') }}</p>
        </div>
        <p v-if="f.tech.length" class="lin-family-tech">{{ f.tech.join(' · ') }}</p>
      </header>
      <lineage-tree :trees="f.trees" :zh="zh" :from="fromPath(f.id)" :horizontal="horizontal" />
    </section>

    <td-timeline :zh="zh" :from="fromPath('td')" />

    <section class="lin-pairs" id="pairs" v-if="pairs.length">
      <header class="lin-section-head">
        <h2>{{ zh ? '雙生' : 'Pairs' }}</h2>
        <p>{{ zh ? `${pairs.length} 對只有兩件的血緣：一件從另一件長出來，然後就停了。` : `${pairs.length} lineages of exactly two: one grew from the other, and it stopped there.` }}</p>
      </header>
      <div class="lin-pair-grid">
        <div v-for="f in pairs" :key="f.id" :id="f.id" class="lin-pair">
          <lineage-tree :trees="f.trees" :zh="zh" :from="fromPath('pairs')" compact :horizontal="horizontal" />
        </div>
      </div>
    </section>

    <section class="lin-templates" id="templates" v-if="data.templates.length">
      <header class="lin-section-head">
        <h2>{{ zh ? '起始模板' : 'Starter templates' }}</h2>
        <p>{{ zh
          ? `${data.templates.length} 支共用的起始模板，${c.startedFromTemplate} 件作品從這裡起步。模板不算祖先：從同一張紙開始畫，不代表畫的是同一件事，所以它們不進上面的樹。`
          : `${data.templates.length} shared starter templates; ${c.startedFromTemplate} works began from one of them. A template is not an ancestor: starting from the same sheet does not make two drawings kin, so they stay out of the trees above.` }}</p>
      </header>
      <ul class="lin-tile-grid">
        <li v-for="t in data.templates" :key="t.id">
          <lineage-card :n="t" :zh="zh" :from="fromPath('templates')" />
          <span v-if="t.started" class="lin-started">{{ zh ? `${t.started} 件從這裡起步` : `${t.started} started here` }}</span>
        </li>
      </ul>
    </section>

    <section class="lin-isolated" id="isolated">
      <header class="lin-section-head">
        <h2>{{ zh ? '獨立的一天' : 'Days that stand alone' }}</h2>
        <p>{{ zh ? `${c.isolated} 件沒有血緣的作品，按年份排。它們不是雜訊：每一件都是某一天真的畫了、但沒有延續下去的東西。` : `${c.isolated} works with no lineage, by year. They are not noise: each is something drawn on a real day that simply was not continued.` }}</p>
      </header>
      <div v-for="y in data.isolated" :key="y.y" class="lin-year">
        <h3>{{ y.y }} <small>{{ y.items.length }}</small></h3>
        <ul class="lin-tile-grid lin-tile-grid-dense">
          <li v-for="n in y.items" :key="n.id"><lineage-card :n="n" :zh="zh" :from="fromPath('isolated')" small /></li>
        </ul>
      </div>
    </section>
  </div>
</template>

<script setup>
import { trackEvent } from '@/runtime/analytics.mjs'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import data from '@/data/lineage.json'
import LineageTree from '@/components/lineage/LineageTree.vue'
import LineageCard from '@/components/lineage/LineageCard.vue'
import TdTimeline from '@/components/lineage/TdTimeline.vue'

const route = useRoute()
const zh = computed(() => route.path.startsWith('/zh/'))
// 2026-09-20：排列方向切換（由上往下＝原本；由左往右＝水平模組，作品頁 lineage mode:'row' 同一套）
const horizontal = ref(false)
const c = data.counts
const big = data.families.filter(f => f.n >= 3)
const pairs = data.families.filter(f => f.n === 2)
const famName = f => (zh.value && f.nameZh) ? f.nameZh : f.name
// 森林裡最大那棵樹的根當家族的臉
const founder = f => f.trees.slice().sort((a, b) => count(b) - count(a))[0]
const count = t => 1 + (t.c || []).reduce((a, x) => a + count(x), 0)
const fromPath = anchor => (zh.value ? '/zh' : '') + '/lineage/#' + anchor
function jump (id) {
  trackEvent('lineage_family_open', { family_id: id })
  const el = document.getElementById(id)
  if (!el) return
  history.replaceState(null, '', '#' + id)
  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<style>
.lineage-page{color:var(--ink,#f4f4f2);width:var(--content-w,min(94vw,1840px));margin:0 auto;padding:40px 0 90px;text-align:left}
.lin-head{max-width:76ch;margin-bottom:34px}
.lin-kicker,.lin-label{font-size:12px;letter-spacing:.1em;text-transform:uppercase;color:var(--ink-3,#85858a);margin:0 0 10px}
.lineage-page h1{font-family:var(--font-display,Georgia,serif);font-size:clamp(2.2rem,4vw,3.6rem);line-height:1.15;margin:0 0 18px}
.lin-intro{font-size:1.05rem;line-height:1.8;margin:0 0 14px}
.lin-intro-td{font-size:.92rem;margin-top:-4px}.lin-intro-td a{color:var(--ink-2,#b4b4b0)}.lin-intro-td a:hover{color:var(--gold,#d9c267)}
.lin-orient{display:flex;align-items:center;gap:8px;font-size:.82rem;color:var(--ink-3,#85858a);margin:0 0 10px}.lin-orient button{font:inherit;font-size:.8rem;letter-spacing:.04em;padding:5px 14px;border:0;border-radius:var(--r-pill);background:var(--tint-2);color:var(--ink-2);cursor:pointer;transition:var(--t-hover)}.lin-orient button:hover{background:var(--tint-3);color:var(--ink)}.lin-orient button[aria-pressed="true"]{background:var(--ink);color:#000}
.lin-legend{display:flex;flex-wrap:wrap;gap:8px 22px;font-size:.82rem;color:var(--ink-2,#b4b4b0);margin:0}
.lin-key{display:inline-flex;align-items:center;gap:8px}
.lin-key i{display:inline-block;width:26px;height:0;border-top:2px solid var(--ink-2,#b4b4b0)}
.lin-key-sim i{border-top-style:dashed}
.lin-key-priv i{width:14px;height:14px;border:0;border-radius:var(--r-sm);background:repeating-linear-gradient(135deg,rgba(217,194,103,.45) 0 3px,transparent 3px 6px),var(--bg2)}
/* 目錄 */
.lin-index{margin:0 0 54px;padding-top:22px;border-top:1px solid var(--line,rgba(255,255,255,.08))}
.lin-index ul{list-style:none;margin:0;padding:0;display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:10px 18px}
.lin-index a{display:grid;grid-template-columns:64px 1fr;gap:12px;align-items:center;color:inherit;text-decoration:none;padding:6px 0;min-height:52px}
.lin-index a:hover strong{color:var(--gold)}
.lin-index-thumb{display:block;aspect-ratio:10/7;background:var(--bg1);overflow:hidden}
.lin-index-thumb img{display:block;width:100%;height:100%;object-fit:cover}
.lin-index-text{display:grid;gap:2px;min-width:0}
.lin-index-text strong{font-weight:500;font-size:.92rem;line-height:1.3;overflow-wrap:anywhere}
.lin-index-text em{font-style:normal;font-size:.74rem;color:var(--ink-3,#85858a)}
/* 家族 */
.lin-family{margin:0 0 72px;padding-top:26px;border-top:1px solid var(--line,rgba(255,255,255,.08));scroll-margin-top:84px;content-visibility:auto;contain-intrinsic-size:auto 900px}
.lin-family-head{display:flex;justify-content:space-between;align-items:flex-end;gap:24px;margin-bottom:22px}
.lin-family-head h2{font-family:var(--font-display,Georgia,serif);font-size:clamp(1.5rem,2.4vw,2.2rem);line-height:1.2;margin:0 0 8px}
.lin-family-head h2 small{font-size:.55em;color:var(--ink-3,#85858a);font-family:var(--font-body,sans-serif);margin-left:10px;font-weight:400}
.lin-family-meta,.lin-family-stems{font-size:.84rem;color:var(--ink-2,#b4b4b0);margin:0 0 4px;line-height:1.6}
.lin-family-stems{color:var(--ink-3,#85858a)}
.lin-family-tech{font-size:.76rem;letter-spacing:.05em;color:var(--ink-3,#85858a);margin:0;white-space:nowrap}
/* 雙生／模板／獨立 */
.lin-section-head{max-width:76ch;margin:0 0 22px}
.lin-pairs,.lin-templates,.lin-isolated{margin:0 0 72px;padding-top:26px;border-top:1px solid var(--line,rgba(255,255,255,.08));scroll-margin-top:84px}
.lin-section-head h2{font-family:var(--font-display,Georgia,serif);font-size:clamp(1.5rem,2.4vw,2.2rem);margin:0 0 8px}
.lin-section-head p{font-size:.92rem;line-height:1.7;color:var(--ink-2,#b4b4b0);margin:0}
.lin-pair-grid{display:flex;flex-wrap:wrap;gap:26px 34px}
.lin-pair{flex:none}
.lin-tile-grid{list-style:none;margin:0;padding:0;display:grid;grid-template-columns:repeat(auto-fill,minmax(132px,1fr));gap:22px 16px}
.lin-tile-grid-dense{grid-template-columns:repeat(auto-fill,minmax(112px,1fr));gap:16px 12px}
.lin-tile-grid li{min-width:0;display:grid;gap:6px;justify-items:start}
.lin-started{font-size:.72rem;color:var(--gold,#d9c267)}
.lin-year{margin:0 0 30px}
.lin-year h3{font-family:var(--font-display,Georgia,serif);font-size:1.15rem;margin:0 0 12px;font-weight:500}
.lin-year h3 small{font-size:.72rem;color:var(--ink-3,#85858a);margin-left:8px;font-family:var(--font-body,sans-serif)}
@media(max-width:767px){
  .lineage-page{padding-top:22px}
  .lin-family-head{flex-direction:column;align-items:flex-start;gap:6px}
  .lin-family-tech{white-space:normal}
  .lin-index ul{grid-template-columns:repeat(auto-fill,minmax(160px,1fr))}
  .lin-index a{grid-template-columns:48px 1fr;gap:9px}
}
</style>
